export const CMS_PORTAL = "cms.todaywithgod.org";
export const CHURCH_PORTAL = "churches.todaywithgod.org";
export const CHANNEL_PORTAL = "channels.todaywithgod.org";
export const MINISTRY_PORTAL = "ministries.todaywithgod.org";
const LOCAL_HOST = "localhost";

const CHURCH_ROUTES = ["/churches", "/churches/all", "/churches/add", "/addfield"];
const CHANNEL_ROUTES = ["/channels", "/channel/all", "/channel/add", "/addChannelField"];
const MINISTRY_ROUTES = ["/ministry", "/ministry/all", "/ministry/add", "/addMinistryField"];

const ROLE_ACCESS_BY_CODE = {
  "39stc8": "all",
  "17dwer8": "church",
  "28qazx9": "channel",
  "31plmk6": "ministry",
};

const ROLE_ACCESS_BY_NAME = {
  "Super Admin": "all",
  "Church Admin": "church",
  "Channel Admin": "channel",
  "Ministry Admin": "ministry",
};

const normalizePortal = (url = "") =>
  String(url).trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "");

export const getHostMode = (host = "", port = "") => {
  const normalizedHost = String(host || "").trim().toLowerCase();
  const normalizedPort = String(port || "").trim();

  if (normalizedHost === CHURCH_PORTAL || (normalizedHost === LOCAL_HOST && normalizedPort === "4000")) {
    return "churches";
  }
  if (normalizedHost === CHANNEL_PORTAL || (normalizedHost === LOCAL_HOST && normalizedPort === "5000")) {
    return "channels";
  }
  if (normalizedHost === MINISTRY_PORTAL || (normalizedHost === LOCAL_HOST && normalizedPort === "6000")) {
    return "ministries";
  }
  return "cms";
};

export const getCurrentHostMode = () => {
  if (typeof window === "undefined") return "cms";
  return getHostMode(window.location.hostname, window.location.port);
};

export const getPortalRootUrlForAccess = (access) => {
  if (typeof window === "undefined") return "/";
  const protocol = window.location.protocol || "https:";
  const host = window.location.hostname || "";

  if (host === LOCAL_HOST) {
    if (access === "church") return `${protocol}//localhost:4000/churches`;
    if (access === "channel") return `${protocol}//localhost:5000/channels`;
    if (access === "ministry") return `${protocol}//localhost:6000/ministry`;
    return `${protocol}//localhost:3000/`;
  }

  if (access === "church") return `${protocol}//${CHURCH_PORTAL}/churches`;
  if (access === "channel") return `${protocol}//${CHANNEL_PORTAL}/channels`;
  if (access === "ministry") return `${protocol}//${MINISTRY_PORTAL}/ministry`;
  return `${protocol}//${CMS_PORTAL}/`;
};

export const resolvePortalAccess = (portalUrls = []) => {
  const normalized = (portalUrls || []).map(normalizePortal);
  const hasCms = normalized.includes(CMS_PORTAL);
  const hasChurch = normalized.includes(CHURCH_PORTAL);
  const hasChannel = normalized.includes(CHANNEL_PORTAL);
  const hasMinistry = normalized.includes(MINISTRY_PORTAL);

  if (hasCms || (hasChurch && hasChannel) || (hasChurch && hasMinistry) || (hasChannel && hasMinistry)) {
    return "all";
  }
  if (hasChurch) return "church";
  if (hasChannel) return "channel";
  if (hasMinistry) return "ministry";
  return "none";
};

export const resolveRolePortalAccess = (profile = {}) => {
  const roleCodeAccess = ROLE_ACCESS_BY_CODE[String(profile?.roleCode || "")];
  if (roleCodeAccess) return roleCodeAccess;

  const roleNameAccess = ROLE_ACCESS_BY_NAME[String(profile?.roleName || "")];
  if (roleNameAccess) return roleNameAccess;

  return "none";
};

export const resolveProfilePortalAccess = (profile = {}) => {
  const portalAccess = resolvePortalAccess(profile?.portalUrls || []);
  if (portalAccess !== "none") return portalAccess;
  return resolveRolePortalAccess(profile);
};

export const pickPreferredCmsProfile = (profiles = []) => {
  if (!Array.isArray(profiles) || profiles.length === 0) return null;
  const weight = { all: 4, church: 3, channel: 2, ministry: 1, none: 0 };

  return profiles
    .map((profile) => ({
      profile,
      access: resolveProfilePortalAccess(profile),
      portalCount: (profile?.portalUrls || []).length,
    }))
    .sort((a, b) => {
      if (weight[b.access] !== weight[a.access]) return weight[b.access] - weight[a.access];
      return b.portalCount - a.portalCount;
    })[0]?.profile;
};

export const getDefaultRouteForAccess = (access, hostMode = getCurrentHostMode()) => {
  // On entity-specific hosts, prevent "all" access from landing on full dashboard.
  if (access === "all") {
    if (hostMode === "churches") return "/churches";
    if (hostMode === "channels") return "/channels";
    if (hostMode === "ministries") return "/ministry";
  }

  if (access === "church") return "/churches";
  if (access === "channel") return "/channels";
  if (access === "ministry") return "/ministry";
  if (access === "none") return "/unauthorized";
  return "/";
};

export const isPathAllowedForAccess = (path, access, hostMode = getCurrentHostMode()) => {
  if (!path) return true;

  const commonAllowed = ["/validate-user", "/logout", "/unauthorized", "/profile", "/agreements/consent"];
  if (commonAllowed.includes(path)) return true;

  const isAllowedByRoutes = (routes) => routes.some((allowed) => path.startsWith(allowed));
  const allEntityRoutes = [...CHURCH_ROUTES, ...CHANNEL_ROUTES, ...MINISTRY_ROUTES];

  // On non-cms hosts, "all" access can use only entity modules (not full dashboard/cms menus).
  if (access === "all") {
    if (hostMode !== "cms") {
      if (hostMode === "churches") return isAllowedByRoutes(CHURCH_ROUTES);
      if (hostMode === "channels") return isAllowedByRoutes(CHANNEL_ROUTES);
      if (hostMode === "ministries") return isAllowedByRoutes(MINISTRY_ROUTES);
      return false;
    }
    return true;
  }

  if (access === "church") {
    return isAllowedByRoutes(CHURCH_ROUTES);
  }

  if (access === "channel") {
    return isAllowedByRoutes(CHANNEL_ROUTES);
  }

  if (access === "ministry") {
    return isAllowedByRoutes(MINISTRY_ROUTES);
  }

  return false;
};
